import { useState, useEffect, useCallback } from 'react'
import { useGameStore } from '@store/gameStore'
import './SecondChanceModal.css'

interface SecondChanceOffer {
    id: string
    title: string
    originalPrice: number
    discountedPrice: number
    discount: string
    items: { emoji: string; text: string }[]
    type: 'shop_exit' | 'battle_lost' | 'energy_empty' | 'gems_low'
}

const OFFERS: Record<string, SecondChanceOffer> = {
    shop_exit: {
        id: 'shop_exit',
        title: 'STARTER POWER PACK',
        originalPrice: 19.99,
        discountedPrice: 4.99,
        discount: '-75%',
        items: [
            { emoji: '💎', text: '1000 Gems' },
            { emoji: '⚔️', text: '+500 Power' },
            { emoji: '👑', text: 'VIP 24h' },
        ],
        type: 'shop_exit',
    },
    battle_lost: {
        id: 'battle_lost',
        title: 'REVENGE PACK',
        originalPrice: 9.99,
        discountedPrice: 2.99,
        discount: '-70%',
        items: [
            { emoji: '⚔️', text: '+1000 Power' },
            { emoji: '💎', text: '200 Gems' },
            { emoji: '⚡', text: 'Volle Energie' },
        ],
        type: 'battle_lost',
    },
    energy_empty: {
        id: 'energy_empty',
        title: 'ENERGIE BOOSTER',
        originalPrice: 4.99,
        discountedPrice: 0.99,
        discount: '-80%',
        items: [
            { emoji: '⚡', text: 'Volle Energie' },
            { emoji: '💎', text: '100 Gems' },
            { emoji: '⏰', text: '+2h Energie-Boost' },
        ],
        type: 'energy_empty',
    },
    gems_low: {
        id: 'gems_low',
        title: 'GEM SAVER PACK',
        originalPrice: 14.99,
        discountedPrice: 3.99,
        discount: '-73%',
        items: [
            { emoji: '💎', text: '500 Gems' },
            { emoji: '🎁', text: 'Bonus Lootbox' },
            { emoji: '⭐', text: 'Doppelte XP 1h' },
        ],
        type: 'gems_low',
    },
}

interface SecondChanceModalProps {
    trigger: 'shop_exit' | 'battle_lost' | 'energy_empty' | 'gems_low'
    onClose: () => void
    isOpen: boolean
}

export function SecondChanceModal({ trigger, onClose, isOpen }: SecondChanceModalProps) {
    const [countdown, setCountdown] = useState(300) // 5 minutes
    const [isClosing, setIsClosing] = useState(false)
    const [purchased, setPurchased] = useState(false)

    const resources = useGameStore(state => state.resources)
    const trackMoneySpent = useGameStore(state => state.trackMoneySpent)
    const addGems = useGameStore(state => state.addGems)
    const addPower = useGameStore(state => state.addPower)
    const activateVIP = useGameStore(state => state.activateVIP)
    const setEnergy = useGameStore(state => state.setEnergy)

    const offer = OFFERS[trigger]

    // Countdown timer
    useEffect(() => {
        if (!isOpen || purchased) return

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer)
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
    }, [isOpen, purchased])

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const handlePurchase = () => {
        trackMoneySpent(offer.discountedPrice)

        // Apply rewards based on offer type
        switch (trigger) {
            case 'shop_exit':
                addGems(1000)
                addPower(500)
                activateVIP(24)
                break
            case 'battle_lost':
                addPower(1000)
                addGems(200)
                setEnergy(resources.maxEnergy)
                break
            case 'energy_empty':
                setEnergy(resources.maxEnergy)
                addGems(100)
                break
            case 'gems_low':
                addGems(500)
                break
        }

        setPurchased(true)
    }

    const handleDecline = useCallback(() => {
        setIsClosing(true)
        setTimeout(() => {
            setIsClosing(false)
            onClose()
            setPurchased(false)
        }, 300)
    }, [onClose])

    if (!isOpen) return null

    return (
        <div className={`second-chance-overlay ${isClosing ? 'second-chance-overlay--closing' : ''}`}>
            <div className="second-chance">
                {!purchased ? (
                    <>
                        {/* Attention Grabber */}
                        <div className="second-chance__alert">
                            <span className="second-chance__alert-icon">⚠️</span>
                            <span className="second-chance__alert-text">WARTE!</span>
                            <span className="second-chance__alert-icon">⚠️</span>
                        </div>

                        {/* Main Content */}
                        <div className="second-chance__content">
                            <h2 className="second-chance__subtitle">Exklusives Angebot nur für DICH!</h2>

                            <div className="second-chance__offer">
                                <span className="second-chance__offer-title">{offer.title}</span>
                                <span className="second-chance__offer-badge">{offer.discount}</span>
                            </div>

                            {/* Items */}
                            <div className="second-chance__items">
                                {offer.items.map((item, idx) => (
                                    <div key={idx} className="second-chance__item">
                                        <span className="second-chance__item-emoji">{item.emoji}</span>
                                        <span className="second-chance__item-text">{item.text}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Price */}
                            <div className="second-chance__price">
                                <span className="second-chance__price-original">{offer.originalPrice.toFixed(2)}€</span>
                                <span className="second-chance__price-arrow">→</span>
                                <span className="second-chance__price-discounted">{offer.discountedPrice.toFixed(2)}€</span>
                            </div>

                            {/* Countdown */}
                            <div className="second-chance__countdown">
                                <span className="second-chance__countdown-icon">⏰</span>
                                <span className="second-chance__countdown-text">
                                    Angebot läuft ab in: {formatTime(countdown)}
                                </span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="second-chance__actions">
                            <button
                                className="second-chance__btn second-chance__btn--buy"
                                onClick={handlePurchase}
                            >
                                💸 JETZT KAUFEN
                            </button>

                            <button
                                className="second-chance__btn second-chance__btn--decline"
                                onClick={handleDecline}
                            >
                                ❌ Nein, ich hasse gute Deals
                            </button>
                        </div>

                        {/* Urgency Indicators */}
                        <div className="second-chance__urgency">
                            <span className="second-chance__urgency-item">🔥 847 Spieler haben heute gekauft</span>
                            <span className="second-chance__urgency-item">⚡ Limitiert auf 1x pro Account</span>
                        </div>
                    </>
                ) : (
                    <div className="second-chance__success">
                        <div className="second-chance__success-icon">🎉</div>
                        <h2 className="second-chance__success-title">ERFOLGREICH!</h2>
                        <p className="second-chance__success-msg">Dein Paket wurde gutgeschrieben.</p>

                        <div className="second-chance__items second-chance__items--success">
                            {offer.items.map((item, idx) => (
                                <div key={idx} className="second-chance__item">
                                    <span className="second-chance__item-emoji">{item.emoji}</span>
                                    <span className="second-chance__item-text">{item.text}</span>
                                </div>
                            ))}
                        </div>

                        <button
                            className="second-chance__btn second-chance__btn--buy"
                            onClick={handleDecline}
                        >
                            🚀 VIEL SPASS!
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}

// Hook to manage second chance modal state
export function useSecondChance() {
    const [isOpen, setIsOpen] = useState(false)
    const [trigger, setTrigger] = useState<SecondChanceModalProps['trigger']>('shop_exit')
    const [hasShown, setHasShown] = useState<Record<string, boolean>>({})

    const show = useCallback((type: SecondChanceModalProps['trigger']) => {
        // Only show once per session per type
        if (hasShown[type]) return

        setTrigger(type)
        setIsOpen(true)
        setHasShown(prev => ({ ...prev, [type]: true }))
    }, [hasShown])

    const hide = useCallback(() => {
        setIsOpen(false)
    }, [])

    return { isOpen, trigger, show, hide }
}
